import React, { useCallback, useContext, useMemo, useState } from "react";
import { useNavigation } from "@react-navigation/native";
import ScreenCard from "./ScreenCard";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../navigations/RootNavigation";
import { useCurrencyFormatter, useI18n } from "../hooks/i18n-hooks";
import { useTheme } from "../hooks/use-theme";
import ListItem from "../ui_atoms/ListItem";
import { StyleSheet, View } from "react-native";
import ListGroup from "../components/ListGroup";
import Text from "../components/Text";
import Separator from "../ui_atoms/separator";
import ToggleView from "../ui_atoms/ToggleView";
import InfoModal from "../components/InfoModal";
import MarkdownView from "../components/MarkdownView";
import Gap from "../ui_atoms/Gap";
import { EarningType, ProfileType, SalaryDeductionType } from "../__generated__/graphql";
import { TitleMediumText } from "../components/FormattedText";
import { ProfileSwitcherManagerContext } from "../contexts/ProfileSwitcherManager";
import { BubbleTalk } from "../components/BubbleTalk";
import DocuIllSvg from "../assets/images/docu_ill.svg";
import VStack from "../ui_atoms/VStack";

type SalaryCountingDetailsScreenProps = StackScreenProps<
    RootStackParamList,
    "SalaryCountingDetails"
>;

const InnerSeparator = () => {
    const { gridSize, colors } = useTheme();
    return (
        <View
            style={{
                marginLeft: gridSize / 2,
                flex: 1,
                height: 1,
                backgroundColor: colors.accentLighter,
            }}
        />
    );
};

const VAT_PERCENT = 8.1; // will be change to use a value from the server
const SalaryCountingDetailsScreen = ({ route }: SalaryCountingDetailsScreenProps) => {
    const { salaryDetails } = route.params;
    const { profileType } = useContext(ProfileSwitcherManagerContext);
    const i18n = useI18n();
    const styles = useStyles();
    const { colors } = useTheme();
    const navigation = useNavigation();
    const currencyFormatter = useCurrencyFormatter();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalHelpKey, setModalHelpKey] = useState("");

    const onDeductionTitlePress = useCallback((helpKey: string) => {
        setIsModalVisible(true);
        setModalHelpKey(helpKey);
    }, []);

    const [isClientDeductionsDetailsVisible, setIsClientDeductionsDetailsVisible] = useState(false);
    const [isWorkerDeductionsDetailsVisible, setIsWorkerDeductionsDetailsVisible] = useState(false);
    const [isCompensationsDetailsVisible, setIsCompensationsDetailsVisible] = useState(false);

    const onClientDeductionsDetailsPress = useCallback(() => {
        setIsClientDeductionsDetailsVisible(!isClientDeductionsDetailsVisible);
    }, [isClientDeductionsDetailsVisible]);

    const onCompensationsDetailsPress = useCallback(() => {
        setIsCompensationsDetailsVisible(!isCompensationsDetailsVisible);
    }, [isCompensationsDetailsVisible]);

    const onWorkerDeductionsDetailsPress = useCallback(() => {
        setIsWorkerDeductionsDetailsVisible(!isWorkerDeductionsDetailsVisible);
    }, [isWorkerDeductionsDetailsVisible]);

    const formatItem = useCallback(
        (deduction: SalaryDeductionType) => {
            return {
                value: deduction.title,
                description: `${i18n.t(
                    `screens.user.salary_counting_details.${deduction.title}.short_title`,
                    { percent: deduction.rate },
                )}`,
                badge: (
                    <Text style={styles.amountLight}>
                        {currencyFormatter({
                            amountInCents: deduction.amount,
                        })}
                    </Text>
                ),
                onPress: () => onDeductionTitlePress(deduction.title),
                inGroup: true,
                rightActionMode: "none" as const,
            };
        },
        [i18n, onDeductionTitlePress, styles.amountLight, currencyFormatter],
    );

    const totalCompensation = useMemo(
        () =>
            (salaryDetails.vacationCompensation || 0) +
            (salaryDetails.publicHolidaysCompensation || 0) +
            (salaryDetails.thirteenMonthCompensation || 0),
        [
            salaryDetails.publicHolidaysCompensation,
            salaryDetails.thirteenMonthCompensation,
            salaryDetails.vacationCompensation,
        ],
    );

    const totalClientDeductions = useMemo(
        () => salaryDetails.clientDeductions?.reduce((result, d) => result + d.amount, 0) || 0,
        [salaryDetails.clientDeductions],
    );

    const totalWorkerDeductions = useMemo(
        () => salaryDetails.workerDeductions?.reduce((result, d) => result + d.amount, 0) || 0,
        [salaryDetails.workerDeductions],
    );

    return (
        <>
            <InfoModal
                visible={isModalVisible}
                title={i18n.t(`screens.user.salary_counting_details.${modalHelpKey}.title`)}
                onClose={() => {
                    setIsModalVisible(false);
                }}
            >
                <MarkdownView
                    markdown={i18n.t(
                        `screens.user.salary_counting_details.${modalHelpKey}.description`,
                    )}
                />
            </InfoModal>

            <VStack h="2/3" v="2/3">
                <BubbleTalk
                    personImageComponent={<DocuIllSvg width="100%" height="100%" />}
                    personSize={44}
                    position="center-right"
                    bubbleBackgroundColor={colors.accentLighter}
                >
                    <TitleMediumText>
                        {i18n.t("screens.user.salary_counting_details.title")}
                    </TitleMediumText>
                </BubbleTalk>
            </VStack>
            <ScreenCard
                buttons={[
                    {
                        title: i18n.t("screens.user.salary_counting_details.close"),
                        onPress: () => navigation.goBack(),
                        mode: "outlined",
                    },
                ]}
            >
                {/* base salary */}
                <ListItem
                    inGroup
                    description={i18n.t("screens.user.salary_counting_details.base_salary")}
                    badge={
                        <Text style={styles.amountLight}>
                            {currencyFormatter({
                                amountInCents: salaryDetails.baseSalary,
                            })}
                        </Text>
                    }
                />
                {[EarningType.Hourly, EarningType.Daily].includes(route.params.earningType) && (
                    <>
                        <ListItem
                            inGroup
                            description={i18n.t(
                                "screens.user.salary_counting_details.holiday_13th_compensations",
                            )}
                            badge={
                                <Text style={[styles.amountLight, styles.amountTop]}>
                                    {currencyFormatter({
                                        amountInCents: totalCompensation,
                                    })}
                                </Text>
                            }
                            labels={[
                                {
                                    text: i18n.t(
                                        `screens.user.salary_counting_details.${
                                            isCompensationsDetailsVisible ? "hide" : "show"
                                        }_details`,
                                    ),
                                    mode: "miniCta",
                                },
                            ]}
                            onPress={onCompensationsDetailsPress}
                            rightActionMode="none"
                        />
                        <ToggleView isOpen={isCompensationsDetailsVisible}>
                            <ListGroup
                                style={{ marginLeft: 10, paddingRight: 10 }}
                                itemStyle={{ paddingVertical: 5 }}
                                isItemsCheckable={false}
                                items={[
                                    {
                                        title: "thirteen_month_compensation",
                                        quantity: 1,
                                        amount: salaryDetails.thirteenMonthCompensation,
                                        rate: "0",
                                    },
                                    {
                                        title: "public_holidays_compensation",
                                        quantity: 1,
                                        amount: salaryDetails.publicHolidaysCompensation,
                                        rate: "0",
                                    },
                                    {
                                        title: "vacations_compensation",
                                        quantity: 1,
                                        amount: salaryDetails.vacationCompensation,
                                        rate: "0",
                                    },
                                ].map(formatItem)}
                            />
                        </ToggleView>
                    </>
                )}
                {route.params.earningType === EarningType.Monthly && (
                    <ListItem
                        inGroup
                        description={i18n.t(
                            "screens.user.salary_counting_details.13th_compensations",
                        )}
                        badge={
                            <Text style={[styles.amountLight, styles.amountTop]}>
                                {currencyFormatter({
                                    amountInCents: totalCompensation,
                                })}
                            </Text>
                        }
                        rightActionMode="none"
                    />
                )}
                <InnerSeparator />
                {/* Gross salary */}
                <ListItem
                    inGroup
                    description={`**${i18n.t(
                        "screens.user.salary_counting_details.gross_salary",
                    )}**`}
                    badge={
                        <Text style={styles.amountBold}>
                            {currencyFormatter({
                                amountInCents: salaryDetails.grossSalary,
                            })}
                        </Text>
                    }
                />
                <Gap gap={1} />
                <TitleMediumText style={styles.title_item}>
                    {i18n.t("screens.user.salary_counting_details.worker_salary_title")}
                </TitleMediumText>
                <Separator />
                <ListItem
                    inGroup
                    description={i18n.t("screens.user.salary_counting_details.gross_salary")}
                    badge={
                        <Text style={styles.amountLight}>
                            {currencyFormatter({
                                amountInCents: salaryDetails.grossSalary,
                            })}
                        </Text>
                    }
                />
                {/* Deduction Worker*/}
                <ListItem
                    inGroup
                    description={i18n.t("screens.user.salary_counting_details.worker_deductions")}
                    badge={
                        <Text style={[styles.amountLight, styles.amountTop]}>
                            -
                            {currencyFormatter({
                                amountInCents: totalWorkerDeductions,
                            })}
                        </Text>
                    }
                    labels={[
                        {
                            text: i18n.t(
                                `screens.user.salary_counting_details.${
                                    isWorkerDeductionsDetailsVisible ? "hide" : "show"
                                }_details`,
                            ),
                            mode: "miniCta",
                        },
                    ]}
                    onPress={onWorkerDeductionsDetailsPress}
                    rightActionMode="none"
                />
                <ToggleView isOpen={isWorkerDeductionsDetailsVisible}>
                    <ListGroup
                        style={{ marginLeft: 10, paddingRight: 10 }}
                        itemStyle={{ paddingVertical: 5 }}
                        isItemsCheckable={false}
                        items={[...(salaryDetails.workerDeductions || [])].map(formatItem)}
                    />
                </ToggleView>
                <InnerSeparator />
                <ListItem
                    inGroup
                    title={`${i18n.t("screens.user.salary_counting_details.net_salary")}`}
                    badge={
                        <Text style={styles.amountBold}>
                            {currencyFormatter({
                                amountInCents: salaryDetails.netSalary,
                            })}
                        </Text>
                    }
                />
                {route.params.earningType === EarningType.Hourly && (
                    <>
                        <ListItem
                            style={{ paddingVertical: 5 }}
                            inGroup
                            disabled
                            description={`${i18n.t(
                                "screens.user.salary_counting_details.paid_salary",
                            )}`}
                            badge={
                                <Text style={styles.amountLightSecondary}>
                                    {currencyFormatter({
                                        amountInCents:
                                            salaryDetails.netSalary -
                                            salaryDetails.vacationCompensation,
                                    })}
                                </Text>
                            }
                        />
                        <ListItem
                            style={{ paddingVertical: 0 }}
                            inGroup
                            disabled
                            description={`${i18n.t(
                                "screens.user.salary_counting_details.vacancy_indemnity",
                            )}`}
                            badge={
                                <Text style={styles.amountLightSecondary}>
                                    {currencyFormatter({
                                        amountInCents: salaryDetails.vacationCompensation,
                                    })}
                                </Text>
                            }
                        />
                    </>
                )}
                <Gap gap={1} />

                <TitleMediumText style={styles.title_item}>
                    {i18n.t("screens.user.salary_counting_details.client_salary_title")}
                </TitleMediumText>
                <Separator />
                <ListItem
                    inGroup
                    description={i18n.t("screens.user.salary_counting_details.gross_salary")}
                    badge={
                        <Text style={styles.amountLight}>
                            {currencyFormatter({
                                amountInCents: salaryDetails.grossSalary,
                            })}
                        </Text>
                    }
                />
                {/* Deduction Client*/}
                <ListItem
                    inGroup
                    description={i18n.t("screens.user.salary_counting_details.client_deductions")}
                    badge={
                        <Text style={[styles.amountLight, styles.amountTop]}>
                            {currencyFormatter({
                                amountInCents: totalClientDeductions,
                            })}
                        </Text>
                    }
                    labels={[
                        {
                            text: i18n.t(
                                `screens.user.salary_counting_details.${
                                    isClientDeductionsDetailsVisible ? "hide" : "show"
                                }_details`,
                            ),
                            mode: "miniCta",
                        },
                    ]}
                    onPress={onClientDeductionsDetailsPress}
                    rightActionMode="none"
                />
                <ToggleView isOpen={isClientDeductionsDetailsVisible}>
                    <ListGroup
                        style={{ marginLeft: 10, paddingRight: 10 }}
                        isItemsCheckable={false}
                        items={[...(salaryDetails.clientDeductions || [])].map(formatItem)}
                    />
                </ToggleView>
                <InnerSeparator />
                <ListItem
                    inGroup
                    title={i18n.t(
                        "screens.user.salary_counting_details.total_with_client_deductions",
                    )}
                    badge={
                        <Text style={styles.amountBold}>
                            {currencyFormatter({
                                amountInCents:
                                    salaryDetails.invoiceSalary - salaryDetails.lseMargin,
                            })}
                        </Text>
                    }
                />
                {profileType === ProfileType.Client && (
                    <>
                        <ListItem
                            inGroup
                            description={i18n.t("screens.user.salary_counting_details.lse_fees")}
                            badge={
                                <Text style={styles.amountLight}>
                                    {currencyFormatter({
                                        amountInCents: salaryDetails.lseMargin,
                                    })}
                                </Text>
                            }
                        />
                        <InnerSeparator />
                        <ListItem
                            inGroup
                            description={`**${i18n.t(
                                "screens.user.salary_counting_details.invoiced_salary",
                            )}**`}
                            badge={
                                <Text style={styles.amountBold}>
                                    {currencyFormatter({
                                        amountInCents: salaryDetails.invoiceSalary,
                                    })}
                                </Text>
                            }
                        />
                        <ListItem
                            inGroup
                            description={i18n.t("screens.user.salary_counting_details.taxes", {
                                percent: VAT_PERCENT,
                            })}
                            badge={
                                <Text style={styles.amountLight}>
                                    {currencyFormatter({
                                        amountInCents: Math.floor(
                                            (salaryDetails.invoiceSalary * VAT_PERCENT) / 100,
                                        ),
                                    })}
                                </Text>
                            }
                        />
                        <InnerSeparator />
                        <ListItem
                            inGroup
                            description={`**${i18n.t(
                                "screens.user.salary_counting_details.invoiced_to_client",
                            )}**`}
                            badge={
                                <Text style={styles.amountBold}>
                                    {currencyFormatter({
                                        amountInCents: Math.floor(
                                            salaryDetails.invoiceSalary * (1 + VAT_PERCENT / 100),
                                        ),
                                    })}
                                </Text>
                            }
                        />
                    </>
                )}
            </ScreenCard>
        </>
    );
};

const useStyles = () => {
    const { gridSize, fonts, colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                amountText: {
                    color: "red",
                },
                listGroup: {
                    marginLeft: 10,
                    width: "auto",
                },
                amountTop: {
                    alignSelf: "flex-start",
                },
                amountBold: {
                    ...fonts.bodyLarge,
                },
                amountLight: {
                    paddingLeft: gridSize / 2,
                    ...fonts.bodyMedium,
                },
                amountLightSecondary: {
                    paddingLeft: gridSize / 2,
                    ...fonts.bodyMedium,
                    color: colors.backdrop,
                },
                firstListItem: {
                    marginTop: gridSize,
                },
                title_item: {
                    paddingHorizontal: gridSize / 2,
                    marginBottom: gridSize / 2,
                },
                info_text: {
                    paddingHorizontal: (2 * gridSize) / 3,
                    color: colors.backdrop,
                },
            }),
        [gridSize, fonts, colors.backdrop],
    );
};

export default SalaryCountingDetailsScreen;
